/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
 import { formatMoney } from '@shopify/theme-currency'

 import { convertToHandle } from '~/helpers/convert'
 import { decodeApiId, transformEdges } from '~/helpers/graphql'
 import countries from '~/helpers/country-codes.json'

 /**
  * Returns a full format country object from the supplied code.
  * @param {String} code - ISO 2 country code.
  * @returns {Object}
  */
 export function formatCountryFromCode(code) {
  if (!countries || !code) {
    return false
  }

  return countries.find(country => country['alpha-2'] === code)
 }

 /**
  * Formats response from product.
  * @param {Object} response - GraphQL response.
  * @returns {Object}
  */
 export function formatProduct(response) {
   const product = response.product

   const url = cnvs.routes.root === '/'
     ? `//${location.host}/products/${product.handle}`
     : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

   const formattedProduct = {
     available: product.available,
     compareAtPrice: formatPrice(product.compareAtPriceRange),
     description: product.descriptionHtml,
     handle: product.handle,
     id: decodeApiId(product.id),
     images: formatImages(product),
     metafields: [
       {
        name: 'internal_sku',
        type: product.internal_sku?.type || false,
        value: product.internal_sku?.value || false,
       },
       {
        name: 'model_description',
        type: product.model_description?.type || false,
        value: product.model_description?.value || false,
       },
       {
        name: 'wash_care_instructions',
        type: 'list',
        value: createWashInstructionArray(product),
       },
       {
         name: 'colour_family_size',
         type: product.colour_family_size?.type || false,
         value: product.colour_family_size?.value || false,
       },
       {
         name: 'review_rating',
         type: product.review_rating?.type || false,
         value: product.review_rating?.value || false,
       },
       {
         name: 'review_rating_count',
         type: product.review_rating_count?.type || false,
         value: product.review_rating_count?.value || false,
       },
       {
         name: 'swatch_code',
         type: product.swatch_code?.type || false,
         value: product.swatch_code?.value || false,
       },
       {
         name: 'swatch_name',
         type: product.swatch_name?.type || false,
         value: product.swatch_name?.value || false,
       },
       {
        name: 'size_guide_image',
        type: product.size_guide_image?.type || 'empty',
        value: product.size_guide_image?.reference?.image?.url || 'empty',
       },
       {
        name: 'size_guide_subtitle',
        type: product.size_guide_subtitle?.type || 'empty',
        value: product.size_guide_subtitle?.value || 'empty',
       },
       {
        name: 'size_guide_table',
        type: product.size_guide_table?.type || 'empty',
        value: product.size_guide_table?.value || 'empty',
       },
       {
         name: 'shop_the_look',
         type: product.shop_the_look?.type || 'empty',
         value: product.shop_the_look?.references?.nodes || 'empty',
       },
       {
         name: 'product_card_string',
         type: product.product_card_string?.type || 'empty',
         value: product.product_card_string?.value || 'empty',
       },
     ],
     isComingSoon: product.coming_soon?.value === 'true',
     options: formatOptions(product),
     price: formatPrice(product.priceRange),
     tags: product.tags,
     title: product.title,
     inventory: product.totalInventory,
     type: product.productType,
     url,
   }

   /**
    * Add additional values which rely on formatted response.
    */
   formattedProduct.featuredImage = formattedProduct.images[0]
   formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
   formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

   formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
     return variant.title.trim().toLowerCase() === 'default title'
   })

   return formattedProduct
 }

 /**
  * Creates an array of wash care instructions.
  * @param  {Object} product - Product object.
  * @returns {Array}
  */
 function createWashInstructionArray(product){
  if (!product){
    return []
  }

  const instructions = []
  for (const [key, value] of Object.entries(product)) {
    if(key.includes('wash_care_') && value?.value){
      instructions.push(value.value)
    }
  }

  return instructions
 }

 /**
  * Formats price.
  * @param {Object} price - Price response.
  * @returns {Object}
  */
 function formatPrice(price) {
   return {
     amount: (Number(price?.minVariantPrice.amount) * 100) ?? false,
     currencyCode: price?.minVariantPrice.currencyCode,
     max: (Number(price?.maxVariantPrice.amount) * 100) ?? false,
     min: (Number(price?.minVariantPrice.amount) * 100) ?? false,
     varies: (price?.maxVariantPrice.amount !== price?.minVariantPrice.amount),
   }
 }

 /**
  * Formats images.
  * @param {Object} response - GraphQL response.
  * @returns {Array}
  */
 function formatImages(product) {
   return transformEdges(product.images).map((image) => {
     const linkedVariant = transformEdges(product.variants).find((variant) => {
       return variant.image.id === image.id
     })

     return {
       alt: image.altText,
       height: image.height,
       id: decodeApiId(image.id),
       src: image.url,
       variant: decodeApiId(linkedVariant?.id) || false,
       width: image.width,
     }
   })
 }

 /**
  * Formats options.
  * @param {Object} response - GraphQL response.
  * @returns {Array}
  */
 function formatOptions(product) {
   return product.options.map((option, index) => {
     return {
       index,
       name: option.name,
       values: option.values,
     }
   })
 }

 /**
  * Formats variants.
  * @param {Object} response - GraphQL response.
  * @param {Boolean} isGiftCard - Product type is gift card.
  * @returns {Array}
  */
 function formatVariants(product, isGiftCard) {
   return transformEdges(product.variants).map((variant) => {
     const formattedVariant = {
       available: variant.available,
       compareAtPrice: {
         amount: (Number(variant.compareAtPriceV2?.amount) * 100) || false,
         currencyCode: variant.compareAtPriceV2?.currencyCode || false,
       },
       id: decodeApiId(variant.id),
       image: decodeApiId(variant.image?.id) || false,
       inventory: variant.quantityAvailable,
       metafields: [
         {
           name: 'dist_id_one',
           type: variant.dist_id_one?.type || false,
           value: variant.dist_id_one?.value || false,
         },
         {
           name: 'dist_id_two',
           type: variant.dist_id_two?.type || false,
           value: variant.dist_id_two?.value || false,
         },
         {
           name: 'swatch_code',
           type: variant.swatch_code?.type || false,
           value: variant.swatch_code?.value || false,
         },
         {
           name: 'swatch_name',
           type: variant.swatch_name?.type || false,
           value: variant.swatch_name?.value || false,
         },
       ],
       price: {
         amount: (Number(variant.priceV2.amount) * 100),
         currencyCode: variant.priceV2.currencyCode,
       },
       selectedOptions: variant.selectedOptions.map((option) => {
         return {
           name: option.name,
           value: option.value,
         }
       }),
       sku: variant.sku,
     }

     /**
      * Want for prices to be formatted before determining title.
      * - Gift cards show the price as their title for multi-currency purposes.
      */
     formattedVariant.title = isGiftCard
       ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
       : variant.title

     return formattedVariant
   })
 }

 /**
  * Formats response from collection.
  * @param {Object} response - GraphQL response.
  * @returns {Object}
  */
 export function formatCollection(response) {
   const collection = response.collection

   const url = cnvs.routes.root === '/'
     ? `//${location.host}/collections/${collection.handle}`
     : `//${location.host}${cnvs.routes.root}/collections/${collection.handle}`

   const formattedCollection = {
     description: collection.descriptionHtml,
     handle: collection.handle,
     id: decodeApiId(collection.id),
     title: collection.title,
     url,
   }

   /**
    * Format products.
    */
   formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
     const product = productResponse

     return {
       handle: product.handle,
     }
   })

   return formattedCollection
 }

 /**
  * Formats response from menu.
  * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
  * @param {String} [previousHandle] - Previous tier's handle.
  * @param {Object} response - GraphQL response or nested items array.
  * @param {Number} [tier] - Current tier number.
  * @returns {Array}
  */
 export function formatMenu({
   prefix = 'mobile',
   previousHandle,
   response,
   tier = 1,
 } = {}) {
   const menu = response?.menu?.items.length ? response.menu.items : response

   return menu.map((object, index) => {
     const handle = previousHandle
       ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
       : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

      const urlObject = new URL(object.url)
      const pathname = urlObject.pathname
      const hrefRef = object.url.includes('#image-group=') ? object.url.split('#image-group=')[1] : false

     const links = object.items?.length
       ? formatMenu({
           prefix,
           previousHandle: handle,
           response: object.items,
           tier: (tier + 1),
         })
       : []

     return {
       columnMultiple: object.columnMultiple,
       handle,
       links,
       tier,
       pathname,
       localisedUrl: object.url,
       hrefRef,
       title: object.title,
       url: object.url,
     }
   })
 }

 /**
 * Format image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  let filetype = src.match(/(?!\.)(?<filetype>jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)/gi)
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    window.console.log('Failed to find image filetype or version')
    return src
  }

  filetype = filetype[0]
  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove filetype.
   * - Remove sizing.
   * - Remove crop.
   * - Remove scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace(`.${filetype}`, '')
    .replace(/_(?:\d+)?x(?:\d+)?/g, '')
    .replace(/_crop_\w+/gi, '')
    .replace(/@\d+x/g, '')

  url = `//${url}.${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}
